import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { currencyConverterStore } from '../stores/currency-converter-store';
import './currency-converter-ui.scss';

export const CurrencyConverterUI = observer(() => {
  const [showHistory, setShowHistory] = useState(false);

  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
  };

  const formatCurrencyValue = (value: number, currencyCode: string) => {
    const currency = currencyConverterStore.currencies.find(c => c.code === currencyCode);
    const symbol = currency?.symbol || currencyCode;
    
    if (currencyCode === 'JPY' || currencyCode === 'KRW') {
      return `${symbol}${Math.round(value).toLocaleString()}`;
    }
    return `${symbol}${value.toFixed(2)}`;
  };

  const getLastUpdatedText = () => {
    const lastUpdated = currencyConverterStore.lastUpdated;
    if (!lastUpdated) return 'Never updated';
    
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - lastUpdated.getTime()) / 60000);
    
    if (diffMinutes < 1) return 'Just updated';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return formatDate(lastUpdated.getTime());
  };

  return (
    <div className="currency-converter">
      <div className="currency-converter__main">
        <div className="currency-converter__header">
          <h2>Currency Converter</h2>
          <div className="currency-converter__header-actions">
            <button
              onClick={() => currencyConverterStore.updateExchangeRates()}
              disabled={currencyConverterStore.isLoading}
              className="currency-converter__btn currency-converter__btn--primary"
            >
              {currencyConverterStore.isLoading ? '⟳ Updating...' : '🔄 Update Rates'}
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`currency-converter__btn currency-converter__btn--secondary ${showHistory ? 'currency-converter__btn--active' : ''}`}
            >
              📋 History
            </button>
          </div>
        </div>

        <div className="currency-converter__status">
          <div className="currency-converter__last-updated">
            Last updated: {getLastUpdatedText()}
          </div>
          {currencyConverterStore.exchangeRate > 0 && (
            <div className="currency-converter__exchange-rate">
              1 {currencyConverterStore.fromCurrency?.code} = {currencyConverterStore.exchangeRate.toFixed(4)} {currencyConverterStore.toCurrency?.code}
            </div>
          )}
        </div>

        <div className="currency-converter__converter">
          <div className="currency-converter__input-section">
            <label className="currency-converter__label">From</label>
            <div className="currency-converter__input-group">
              <input
                type="number"
                value={currencyConverterStore.inputAmount}
                onChange={(e) => currencyConverterStore.setInputAmount(e.target.value)}
                placeholder="Enter amount..."
                className="currency-converter__input"
                step="0.01"
                min="0"
              />
              <select
                value={currencyConverterStore.fromCurrency?.code || ''}
                onChange={(e) => currencyConverterStore.setFromCurrency(e.target.value)}
                className="currency-converter__select"
              >
                {currencyConverterStore.currencies.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="currency-converter__swap-section">
            <button
              onClick={() => currencyConverterStore.swapCurrencies()}
              className="currency-converter__swap-btn"
              title="Swap currencies"
            >
              ⇅
            </button>
          </div>

          <div className="currency-converter__output-section">
            <label className="currency-converter__label">To</label>
            <div className="currency-converter__input-group">
              <div className="currency-converter__result">
                {formatCurrencyValue(currencyConverterStore.result, currencyConverterStore.toCurrency?.code || '')}
              </div>
              <select
                value={currencyConverterStore.toCurrency?.code || ''}
                onChange={(e) => currencyConverterStore.setToCurrency(e.target.value)}
                className="currency-converter__select"
              >
                {currencyConverterStore.currencies.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="currency-converter__conversion-summary">
          {currencyConverterStore.fromCurrency && currencyConverterStore.toCurrency && (
            <div className="currency-converter__summary-text">
              {formatCurrencyValue(parseFloat(currencyConverterStore.inputAmount) || 0, currencyConverterStore.fromCurrency.code)} = {' '}
              <strong>{formatCurrencyValue(currencyConverterStore.result, currencyConverterStore.toCurrency.code)}</strong>
            </div>
          )}
        </div>

        <div className="currency-converter__popular-pairs">
          <h3>Popular Currency Pairs</h3>
          <div className="currency-converter__pairs-grid">
            {currencyConverterStore.popularPairs.map((pair, index) => {
              const fromCurrency = currencyConverterStore.currencies.find(c => c.code === pair.from);
              const toCurrency = currencyConverterStore.currencies.find(c => c.code === pair.to);
              
              if (!fromCurrency || !toCurrency) return null;
              
              const rate = (1 / fromCurrency.rate) * toCurrency.rate;
              
              return (
                <div
                  key={index}
                  onClick={() => currencyConverterStore.setPopularPair(pair.from, pair.to)}
                  className="currency-converter__pair-item"
                >
                  <div className="currency-converter__pair-label">{pair.label}</div>
                  <div className="currency-converter__pair-rate">
                    {rate.toFixed(4)}
                  </div>
                  <div className="currency-converter__pair-symbols">
                    {fromCurrency.symbol} → {toCurrency.symbol}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="currency-converter__currency-grid">
          <h3>All Currencies</h3>
          <div className="currency-converter__currencies">
            {currencyConverterStore.currencies.map((currency) => {
              return (
                <div
                  key={currency.code}
                  className="currency-converter__currency-item"
                >
                  <div className="currency-converter__currency-header">
                    <span className="currency-converter__currency-code">{currency.code}</span>
                    <span className="currency-converter__currency-symbol">{currency.symbol}</span>
                  </div>
                  <div className="currency-converter__currency-name">{currency.name}</div>
                  <div className="currency-converter__currency-rate">
                    1 USD = {currency.rate.toFixed(currency.code === 'JPY' || currency.code === 'KRW' ? 0 : 4)} {currency.code}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showHistory && (
        <div className="currency-converter__history">
          <div className="currency-converter__history-header">
            <h3>Conversion History</h3>
            <div className="currency-converter__history-actions">
              <button
                onClick={() => currencyConverterStore.clearHistory()}
                className="currency-converter__btn currency-converter__btn--danger currency-converter__btn--small"
                disabled={currencyConverterStore.history.length === 0}
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="currency-converter__history-list">
            {currencyConverterStore.history.length === 0 ? (
              <div className="currency-converter__history-empty">
                No conversion history yet
              </div>
            ) : (
              currencyConverterStore.history.map((item) => (
                <div
                  key={item.id}
                  onClick={() => currencyConverterStore.useHistoryConversion(item)}
                  className="currency-converter__history-item"
                >
                  <div className="currency-converter__history-conversion">
                    <div className="currency-converter__history-from">
                      {formatCurrencyValue(item.fromAmount, item.fromCurrency.code)}
                    </div>
                    <div className="currency-converter__history-arrow">→</div>
                    <div className="currency-converter__history-to">
                      {formatCurrencyValue(item.toAmount, item.toCurrency.code)}
                    </div>
                  </div>
                  <div className="currency-converter__history-meta">
                    <span className="currency-converter__history-rate">
                      Rate: {item.rate.toFixed(4)}
                    </span>
                    <span className="currency-converter__history-date">
                      {formatDate(item.timestamp)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        currencyConverterStore.deleteHistoryItem(item.id);
                      }}
                      className="currency-converter__history-delete"
                      title="Delete this conversion"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
});
